import {
	keepPreviousData,
	useInfiniteQuery,
	useMutation,
	useQueryClient,
} from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Hourglass, Loader2 } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
	CrmLoadError,
	CrmPageHeader,
	CrmRoleNotice,
} from "@/components/crm/crm-page";
import { LoadMore, nextOffset } from "@/components/crm/load-more";
import { PipelineTable } from "@/components/crm/pipeline-table";
import { DsrStatusBadge, ToneBadge } from "@/components/crm/status-badges";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCrmPermissions } from "@/hooks/use-crm-access";
import { useRegional } from "@/hooks/use-regional";
import {
	contactName,
	daysUntil,
	decodeCrmError,
	isErasedName,
} from "@/lib/crm/format";
import { CRM_STALE_TIME, invalidateCrmData } from "@/lib/crm/queries";
import { formatDate } from "@/lib/crm/regional";
import { CRM_TAB_TRIGGER, CRM_TABS_LIST } from "@/lib/crm/tabs";
import type { CrmStaleContactRow } from "@/lib/crm/types";
import { orpc } from "@/utils/orpc";

export const Route = createFileRoute("/crm/reports")({
	component: ReportsPage,
});

const PAGE_SIZE = 50;

type Tab = "needs_action" | "stale" | "retention" | "dsr";
const TABS: Tab[] = ["needs_action", "stale", "retention", "dsr"];

function ReportsPage(): ReactElement {
	const { t } = useTranslation();
	const { canManage } = useCrmPermissions();
	const [tab, setTab] = useState<Tab>("needs_action");

	if (!canManage) return <CrmRoleNotice />;

	return (
		<div className="space-y-4">
			<CrmPageHeader
				title={t("admin:crm.reports.title")}
				subtitle={t("admin:crm.reports.subtitle")}
			/>
			<Tabs value={tab} onValueChange={(value) => setTab(value as Tab)}>
				<TabsList className={CRM_TABS_LIST}>
					{TABS.map((value) => (
						<TabsTrigger key={value} value={value} className={CRM_TAB_TRIGGER}>
							{t(`admin:crm.reports.tabs.${value}`)}
						</TabsTrigger>
					))}
				</TabsList>
				<TabsContent value="needs_action" className="pt-2">
					{tab === "needs_action" && <NeedsActionReport />}
				</TabsContent>
				<TabsContent value="stale" className="pt-2">
					{tab === "stale" && <StaleReport kind="stale" />}
				</TabsContent>
				<TabsContent value="retention" className="pt-2">
					{tab === "retention" && <StaleReport kind="retention" />}
				</TabsContent>
				<TabsContent value="dsr" className="pt-2">
					{tab === "dsr" && <DsrReport />}
				</TabsContent>
			</Tabs>
		</div>
	);
}

const ReportSkeleton = (): ReactElement => (
	<div className="space-y-2">
		<Skeleton className="h-10" />
		<Skeleton className="h-10" />
	</div>
);

const NeedsActionReport = (): ReactElement => {
	const { t } = useTranslation();
	const query = useInfiniteQuery({
		...orpc.admin.crm.reports.needsAction.infiniteOptions({
			input: (offset: number) => ({ limit: PAGE_SIZE, offset }),
			initialPageParam: 0,
			getNextPageParam: (lastPage, pages) =>
				nextOffset(pages, lastPage.total, (page) => page.opportunities.length),
		}),
		staleTime: CRM_STALE_TIME,
		placeholderData: keepPreviousData,
	});
	if (query.isLoading) return <ReportSkeleton />;
	if (query.isError || !query.data) return <CrmLoadError />;
	const rows = query.data.pages.flatMap((page) => page.opportunities);
	const total = query.data.pages[0]?.total ?? 0;
	return (
		<div className="space-y-3">
			<p className="text-muted-foreground text-sm">
				{t("admin:crm.reports.needs_action_hint")}
			</p>
			<PipelineTable opportunities={rows} canManage editableStage={false} />
			<LoadMore
				loaded={rows.length}
				total={total}
				hasMore={query.hasNextPage}
				loading={query.isFetchingNextPage}
				onLoadMore={() => void query.fetchNextPage()}
			/>
		</div>
	);
};

/** "Keep" after a retention review: restarts the contact's retention clock. */
const KeepButton = ({ row }: { row: CrmStaleContactRow }): ReactElement => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const keep = useMutation(orpc.admin.crm.contacts.keep.mutationOptions());
	const name = contactName(row) ?? "—";
	return (
		<Button
			size="sm"
			variant="outline"
			disabled={keep.isPending}
			title={t("admin:crm.reports.keep_named", { name })}
			aria-label={t("admin:crm.reports.keep_named", { name })}
			onClick={async () => {
				try {
					await keep.mutateAsync({ id: row.id });
					await invalidateCrmData(queryClient);
					toast.success(t("admin:crm.reports.kept"));
				} catch (error) {
					toast.error(
						decodeCrmError(error).message ?? t("admin:crm.errors.generic"),
					);
				}
			}}
		>
			{keep.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
			{t("admin:crm.reports.keep")}
		</Button>
	);
};

const StaleRows = ({ rows }: { rows: CrmStaleContactRow[] }): ReactElement => {
	const { t } = useTranslation();
	const regional = useRegional();
	return (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead>{t("admin:crm.contact.name")}</TableHead>
					<TableHead>{t("admin:crm.contact.company")}</TableHead>
					<TableHead>{t("admin:crm.contacts.last_activity")}</TableHead>
					<TableHead>{t("admin:crm.reports.stale_since")}</TableHead>
					<TableHead>{t("admin:crm.reports.erase_on")}</TableHead>
					<TableHead>{t("admin:crm.common.owner")}</TableHead>
					<TableHead>
						<span className="sr-only">{t("admin:crm.reports.keep")}</span>
					</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{rows.map((row) => {
					const days = row.eraseAt ? daysUntil(row.eraseAt) : null;
					return (
						<TableRow key={row.id}>
							<TableCell>
								<Link
									to="/crm/contacts/$contactId"
									params={{ contactId: row.id }}
									className="font-medium hover:underline"
								>
									{contactName(row) ?? "—"}
								</Link>
							</TableCell>
							<TableCell>
								{row.organizationId ? (
									<Link
										to="/crm/organizations/$organizationId"
										params={{ organizationId: row.organizationId }}
										className="hover:underline"
									>
										{row.organizationName}
									</Link>
								) : (
									"—"
								)}
							</TableCell>
							<TableCell>{formatDate(row.lastActivityAt, regional)}</TableCell>
							<TableCell>{formatDate(row.staleSince, regional)}</TableCell>
							<TableCell>
								{row.eraseAt ? (
									<span className="flex items-center gap-2">
										{formatDate(row.eraseAt, regional)}
										{days !== null && (
											<ToneBadge
												tone={days <= 7 ? "rose" : "amber"}
												icon={Hourglass}
											>
												{t("admin:crm.reports.days_left", {
													count: Math.max(0, days),
												})}
											</ToneBadge>
										)}
									</span>
								) : (
									"—"
								)}
							</TableCell>
							<TableCell>{row.ownerName ?? "—"}</TableCell>
							<TableCell className="text-right">
								<KeepButton row={row} />
							</TableCell>
						</TableRow>
					);
				})}
			</TableBody>
		</Table>
	);
};

const StaleReport = ({
	kind,
}: {
	kind: "stale" | "retention";
}): ReactElement => {
	const { t } = useTranslation();
	const paging = {
		input: (offset: number) => ({ limit: PAGE_SIZE, offset }),
		initialPageParam: 0,
		getNextPageParam: (
			lastPage: { contacts: unknown[]; total: number },
			pages: { contacts: unknown[]; total: number }[],
		) => nextOffset(pages, lastPage.total, (page) => page.contacts.length),
	};
	const stale = useInfiniteQuery({
		...orpc.admin.crm.reports.stale.infiniteOptions(paging),
		enabled: kind === "stale",
		staleTime: CRM_STALE_TIME,
		placeholderData: keepPreviousData,
	});
	const retention = useInfiniteQuery({
		...orpc.admin.crm.reports.retentionQueue.infiniteOptions(paging),
		enabled: kind === "retention",
		staleTime: CRM_STALE_TIME,
		placeholderData: keepPreviousData,
	});
	const query = kind === "stale" ? stale : retention;
	if (query.isLoading) return <ReportSkeleton />;
	if (query.isError || !query.data) return <CrmLoadError />;
	const rows = query.data.pages.flatMap((page) => page.contacts);
	const total = query.data.pages[0]?.total ?? 0;
	return (
		<div className="space-y-3">
			<p className="text-muted-foreground text-sm">
				{t(
					kind === "stale"
						? "admin:crm.reports.stale_hint"
						: "admin:crm.reports.retention_hint",
				)}
			</p>
			{rows.length === 0 ? (
				<p className="py-8 text-center text-muted-foreground text-sm">
					{t("admin:crm.reports.empty")}
				</p>
			) : (
				<StaleRows rows={rows} />
			)}
			<LoadMore
				loaded={rows.length}
				total={total}
				hasMore={query.hasNextPage}
				loading={query.isFetchingNextPage}
				onLoadMore={() => void query.fetchNextPage()}
			/>
		</div>
	);
};

const DsrReport = (): ReactElement => {
	const { t } = useTranslation();
	const regional = useRegional();
	const query = useInfiniteQuery({
		...orpc.admin.crm.dsr.list.infiniteOptions({
			input: (offset: number) => ({ limit: PAGE_SIZE, offset }),
			initialPageParam: 0,
			getNextPageParam: (lastPage, pages) =>
				nextOffset(pages, lastPage.total, (page) => page.requests.length),
		}),
		staleTime: CRM_STALE_TIME,
		placeholderData: keepPreviousData,
	});
	if (query.isLoading) return <ReportSkeleton />;
	if (query.isError || !query.data) return <CrmLoadError />;
	const rows = query.data.pages.flatMap((page) => page.requests);
	const total = query.data.pages[0]?.total ?? 0;
	if (rows.length === 0) {
		return (
			<p className="py-8 text-center text-muted-foreground text-sm">
				{t("admin:crm.reports.empty")}
			</p>
		);
	}
	return (
		<div className="space-y-3">
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>{t("admin:crm.dsr.kind")}</TableHead>
						<TableHead>{t("admin:crm.dsr.status")}</TableHead>
						<TableHead>{t("admin:crm.dsr.contact")}</TableHead>
						<TableHead>{t("admin:crm.dsr.requested_via")}</TableHead>
						<TableHead>{t("admin:crm.dsr.verification")}</TableHead>
						<TableHead>{t("admin:crm.dsr.created")}</TableHead>
						<TableHead>{t("admin:crm.dsr.due")}</TableHead>
						<TableHead>{t("admin:crm.dsr.completed")}</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{rows.map((request) => (
						<TableRow key={request.id}>
							<TableCell>{t(`admin:crm.dsr.kinds.${request.kind}`)}</TableCell>
							<TableCell>
								<DsrStatusBadge status={request.status} />
							</TableCell>
							<TableCell>
								{request.contactId ? (
									<Link
										to="/crm/contacts/$contactId"
										params={{ contactId: request.contactId }}
										className="hover:underline"
									>
										{contactName({
											firstName: request.contactFirstName,
											lastName: request.contactLastName,
										}) ??
											(isErasedName({
												firstName: request.contactFirstName,
												lastName: request.contactLastName,
											})
												? t("admin:crm.contact.erased_name")
												: "—")}
									</Link>
								) : (
									"—"
								)}
							</TableCell>
							<TableCell>
								{t(`admin:crm.dsr.via.${request.requestedVia}`, {
									defaultValue: request.requestedVia,
								})}
								{request.requestedByName && (
									<span className="block text-muted-foreground text-xs">
										{request.requestedByName}
									</span>
								)}
							</TableCell>
							<TableCell>
								{t(
									`admin:crm.dsr.verification_methods.${request.verificationMethod}`,
									{
										defaultValue: request.verificationMethod,
									},
								)}
								{request.overrideJustification && (
									<span
										className="block max-w-xs truncate text-muted-foreground text-xs"
										title={request.overrideJustification}
									>
										{request.overrideJustification}
									</span>
								)}
							</TableCell>
							<TableCell>{formatDate(request.createdAt, regional)}</TableCell>
							<TableCell>{formatDate(request.dueAt, regional)}</TableCell>
							<TableCell>{formatDate(request.completedAt, regional)}</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
			<LoadMore
				loaded={rows.length}
				total={total}
				hasMore={query.hasNextPage}
				loading={query.isFetchingNextPage}
				onLoadMore={() => void query.fetchNextPage()}
			/>
		</div>
	);
};
